import React, { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from 'react';
import { useTranslation } from '../lib/LanguageContext';
import { htmlToMd, normalizeUrl } from '../lib/htmlToMd';
import { toggleInlineMarker, toggleLinePrefix, shiftIndent, TextEdit } from '../lib/mdToolbar';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Strikethrough,
  Link as LinkIcon,
  List,
  ListOrdered,
  ListTodo,
  Code,
  Code2,
  Table as TableIcon,
  X,
  Maximize2,
  Minimize2,
  Heading2,
  Heading3,
  Quote,
  Indent,
  Outdent,
  Eye,
  EyeOff,
  Undo2,
  Sparkles,
} from 'lucide-react';

const InCodeBlock = createContext(false);

const MD_COMPONENTS: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  h1: ({ children }) => <h1 className="text-2xl font-bold mb-3 mt-4">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-semibold mb-2 mt-4">{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-semibold mb-2 mt-3">{children}</h3>,
  p:  ({ children }) => <p className="mb-2">{children}</p>,
  // A list may mix task items and plain items, so the bullet is suppressed
  // per task item rather than on the whole list. The checkbox sits in the
  // bullet's gutter so nested lists still stack below the item.
  ul: ({ children }) => <ul className="list-disc ml-4 mb-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal ml-4 mb-2 space-y-1">{children}</ol>,
  li: ({ className, children }) => className?.includes('task-list-item')
    ? <li className="list-none relative -ml-4 pl-5">{children}</li>
    : <li>{children}</li>,
  input: ({ checked, type }) => type === 'checkbox'
    ? <input type="checkbox" checked={!!checked} readOnly className="absolute left-0 top-1.5 accent-indigo-600" />
    : null,
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-300 pl-4 my-2 text-gray-600 italic">{children}</blockquote>,
  a: ({ href, children }) => <a href={normalizeUrl(href)} className="text-indigo-600 underline" target="_blank" rel="noopener noreferrer">{children}</a>,
  pre: ({ children }) => (
    <InCodeBlock.Provider value={true}>
      <pre className="bg-gray-800 text-gray-100 rounded-lg p-4 my-2 overflow-x-auto font-mono text-sm">{children}</pre>
    </InCodeBlock.Provider>
  ),
  code: ({ children }) => {
    const inCodeBlock = useContext(InCodeBlock);
    return inCodeBlock
      ? <code className="font-mono text-sm">{children}</code>
      : <code className="bg-gray-100 rounded px-1 font-mono text-sm">{children}</code>;
  },
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="border-collapse text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border border-gray-300 px-2 py-1 bg-gray-100 font-semibold text-left">{children}</th>,
  td: ({ children }) => <td className="border border-gray-300 px-2 py-1 align-top">{children}</td>,
};
import { NotesAIAssistant } from './NotesAIAssistant';

interface NotesEditorProps {
  content: string;
  onClose: () => void;
  onSave: (content: string) => void;
}

interface DialogSize {
  width: number;
  height: number;
}

const SIZE_STORAGE_KEY = 'notesEditorSize';
const MIN_WIDTH = 420;
const MIN_HEIGHT = 360;
const VIEWPORT_MARGIN = 32;

const clampSize = (size: DialogSize): DialogSize => ({
  width: Math.max(MIN_WIDTH, Math.min(size.width, window.innerWidth - VIEWPORT_MARGIN)),
  height: Math.max(MIN_HEIGHT, Math.min(size.height, window.innerHeight - VIEWPORT_MARGIN)),
});

const loadStoredSize = (): DialogSize | null => {
  try {
    const raw = localStorage.getItem(SIZE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.width !== 'number' || typeof parsed?.height !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

const storeSize = (size: DialogSize | null) => {
  try {
    if (size) localStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify(size));
    else localStorage.removeItem(SIZE_STORAGE_KEY);
  } catch {
    // storage unavailable — size just won't be remembered
  }
};

interface HistorySnapshot {
  value: string;
  selStart: number;
  selEnd: number;
}

const MenuButton = ({
  onClick,
  children,
  title,
}: {
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
}) => (
  <button
    onClick={onClick}
    title={title}
    className="p-2 rounded hover:bg-gray-100 text-gray-700"
  >
    {children}
  </button>
);

export function NotesEditor({ content: initialContent, onClose, onSave }: NotesEditorProps) {
  const { t } = useTranslation();
  const originalMd = useMemo(() => htmlToMd(initialContent), [initialContent]);
  const [content, setContent] = useState(() => htmlToMd(initialContent));
  const [isMaximized, setIsMaximized] = useState(false);
  // User-chosen dialog size from dragging the corner handle (null = default size).
  const [customSize, setCustomSize] = useState<DialogSize | null>(loadStoredSize);
  const dialogRef = useRef<HTMLDivElement>(null);
  const resizeStartRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
  // Open existing notes in preview first; open empty notes straight in edit mode.
  const [showPreview, setShowPreview] = useState(() => originalMd.trim().length > 0);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  // Table-insert popover: lets the user pick how many columns before inserting.
  const [showTablePopover, setShowTablePopover] = useState(false);
  const [tableCols, setTableCols] = useState(3);
  // Holds the note content from before the last AI rewrite, so it can be restored.
  const [preAIContent, setPreAIContent] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Custom undo/redo history — a controlled textarea loses the browser's
  // native undo stack, so we maintain our own.
  const undoStackRef = useRef<HistorySnapshot[]>([]);
  const redoStackRef = useRef<HistorySnapshot[]>([]);
  const lastKindRef = useRef<'type' | 'action' | null>(null);
  const lastTimeRef = useRef(0);

  // Record the state BEFORE a change. Consecutive typing within a short
  // window is coalesced into a single undo step.
  const recordSnapshot = useCallback((snapshot: HistorySnapshot, kind: 'type' | 'action') => {
    const now = Date.now();
    const coalesce =
      kind === 'type' && lastKindRef.current === 'type' && now - lastTimeRef.current < 500;
    if (!coalesce) {
      undoStackRef.current.push(snapshot);
      if (undoStackRef.current.length > 200) undoStackRef.current.shift();
    }
    redoStackRef.current = [];
    lastKindRef.current = kind;
    lastTimeRef.current = now;
  }, []);

  const restoreSelection = useCallback((snapshot: HistorySnapshot) => {
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      const s = Math.min(snapshot.selStart, snapshot.value.length);
      const e = Math.min(snapshot.selEnd, snapshot.value.length);
      ta.setSelectionRange(s, e);
    });
  }, []);

  const undo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const ta = textareaRef.current;
    const current: HistorySnapshot = {
      value: content,
      selStart: ta?.selectionStart ?? content.length,
      selEnd: ta?.selectionEnd ?? content.length,
    };
    const prev = undoStackRef.current.pop()!;
    redoStackRef.current.push(current);
    lastKindRef.current = null; // break typing coalescing after an undo
    setContent(prev.value);
    restoreSelection(prev);
  }, [content, restoreSelection]);

  const redo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    const ta = textareaRef.current;
    const current: HistorySnapshot = {
      value: content,
      selStart: ta?.selectionStart ?? content.length,
      selEnd: ta?.selectionEnd ?? content.length,
    };
    const next = redoStackRef.current.pop()!;
    undoStackRef.current.push(current);
    lastKindRef.current = null;
    setContent(next.value);
    restoreSelection(next);
  }, [content, restoreSelection]);

  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const ta = e.target;
    // snapshot the state BEFORE this keystroke (content still holds the old value)
    recordSnapshot({ value: content, selStart: ta.selectionStart, selEnd: ta.selectionEnd }, 'type');
    setContent(ta.value);
  }, [content, recordSnapshot]);

  const hasChanges = content !== originalMd;

  const handleClose = useCallback(() => {
    if (hasChanges) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  }, [hasChanges, onClose]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleClose]);

  // Insert MD syntax around selection or at cursor
  const insertMd = useCallback((before: string, after = '') => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = ta.value.slice(start, end);
    const newValue = ta.value.slice(0, start) + before + selected + after + ta.value.slice(end);
    recordSnapshot({ value: ta.value, selStart: start, selEnd: end }, 'action');
    setContent(newValue);
    requestAnimationFrame(() => {
      ta.focus();
      if (selected) {
        ta.setSelectionRange(start + before.length, start + before.length + selected.length);
      } else {
        ta.setSelectionRange(start + before.length, start + before.length);
      }
    });
  }, [recordSnapshot]);

  // Apply a toolbar transform to the textarea as one undoable step.
  const applyEdit = useCallback((transform: (value: string, selStart: number, selEnd: number) => TextEdit) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const edit = transform(ta.value, start, end);
    recordSnapshot({ value: ta.value, selStart: start, selEnd: end }, 'action');
    setContent(edit.value);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(edit.selStart, edit.selEnd);
    });
  }, [recordSnapshot]);

  // Toggle a symmetric inline marker (bold, italic, …) around the selection
  const toggleMarker = useCallback((marker: string) => {
    applyEdit((v, s, e) => toggleInlineMarker(v, s, e, marker));
  }, [applyEdit]);

  // Toggle a block prefix (heading, list, quote) on every selected line
  const togglePrefix = useCallback((prefix: string) => {
    applyEdit((v, s, e) => toggleLinePrefix(v, s, e, prefix));
  }, [applyEdit]);

  // Shift the selected lines one nesting level in or out
  const shiftLines = useCallback((direction: 1 | -1) => {
    applyEdit((v, s, e) => shiftIndent(v, s, e, direction));
  }, [applyEdit]);

  const handleTextareaKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      shiftLines(e.shiftKey ? -1 : 1);
      return;
    }
    const mod = e.metaKey || e.ctrlKey;
    if (!mod) return;
    const key = e.key.toLowerCase();
    if (key === 'z') {
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    } else if (key === 'y') {
      e.preventDefault();
      redo();
    }
  }, [undo, redo, shiftLines]);

  // Insert a GFM table with `cols` columns: one header row + one empty row.
  const insertTable = useCallback((cols: number) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const n = Math.max(1, Math.min(cols, 10));
    const headers = Array.from({ length: n }, (_, i) => `${t('mdTableColumnPrefix')} ${i + 1}`);
    const headerRow = `| ${headers.join(' | ')} |`;
    const separatorRow = `| ${Array.from({ length: n }, () => '---').join(' | ')} |`;
    const emptyRow = `| ${Array.from({ length: n }, () => '   ').join(' | ')} |`;
    const table = `${headerRow}\n${separatorRow}\n${emptyRow}\n`;

    const start = ta.selectionStart;
    const before = ta.value.slice(0, start);
    // A GFM table must start its own block, i.e. be preceded by a blank line.
    let leading = '';
    if (before.length > 0 && !before.endsWith('\n\n')) {
      leading = before.endsWith('\n') ? '\n' : '\n\n';
    }
    const insertText = leading + table;
    const newValue = before + insertText + ta.value.slice(ta.selectionEnd);

    recordSnapshot({ value: ta.value, selStart: start, selEnd: ta.selectionEnd }, 'action');
    setContent(newValue);

    // Select the first header cell text so the user can immediately rename it.
    const firstHeaderPos = before.length + leading.length + 2; // skip "| "
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(firstHeaderPos, firstHeaderPos + headers[0].length);
    });
  }, [recordSnapshot]);

  // Called by AI assistant with the full generated MD content.
  // Replaces the whole note: a new write when empty, an update when not.
  const handleAIResult = useCallback((mdContent: string) => {
    const ta = textareaRef.current;
    const before = ta?.value ?? '';
    recordSnapshot(
      {
        value: before,
        selStart: ta?.selectionStart ?? 0,
        selEnd: ta?.selectionEnd ?? 0,
      },
      'action'
    );
    setPreAIContent(before);
    setContent(mdContent);
    requestAnimationFrame(() => {
      const target = textareaRef.current;
      if (target) {
        target.focus();
        target.setSelectionRange(mdContent.length, mdContent.length);
      }
    });
  }, [recordSnapshot]);

  // Restore the note to the text it had before the last AI rewrite.
  const revertAIResult = useCallback(() => {
    if (preAIContent === null) return;
    const ta = textareaRef.current;
    recordSnapshot(
      {
        value: ta?.value ?? content,
        selStart: ta?.selectionStart ?? 0,
        selEnd: ta?.selectionEnd ?? 0,
      },
      'action'
    );
    setContent(preAIContent);
    const restored = preAIContent;
    setPreAIContent(null);
    requestAnimationFrame(() => {
      const target = textareaRef.current;
      if (target) {
        target.focus();
        target.setSelectionRange(restored.length, restored.length);
      }
    });
  }, [preAIContent, content, recordSnapshot]);

  // Corner-handle resizing. The dialog is centered, so it grows in both
  // directions — doubling the pointer delta keeps the corner under the cursor.
  const handleResizeStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = dialog.getBoundingClientRect();
    resizeStartRef.current = { x: e.clientX, y: e.clientY, width: rect.width, height: rect.height };
  }, []);

  const handleResizeMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const start = resizeStartRef.current;
    if (!start) return;
    setCustomSize(clampSize({
      width: start.width + (e.clientX - start.x) * 2,
      height: start.height + (e.clientY - start.y) * 2,
    }));
  }, []);

  const handleResizeEnd = useCallback(() => {
    if (!resizeStartRef.current) return;
    resizeStartRef.current = null;
    setCustomSize(size => {
      storeSize(size);
      return size;
    });
  }, []);

  const resetSize = useCallback(() => {
    setCustomSize(null);
    storeSize(null);
  }, []);

  const isCustomSized = !isMaximized && customSize !== null;
  const fillsHeight = isMaximized || isCustomSized;

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex z-50 ${isMaximized ? 'p-4' : 'items-center justify-center'}`}>
      <div
        ref={dialogRef}
        className={`relative bg-white rounded-lg flex flex-col w-full ${
          isMaximized ? 'h-full' : isCustomSized ? 'mx-4' : 'max-w-2xl mx-4'
        }`}
        style={isCustomSized ? {
          width: customSize.width,
          height: customSize.height,
          maxWidth: `calc(100vw - ${VIEWPORT_MARGIN}px)`,
          maxHeight: `calc(100vh - ${VIEWPORT_MARGIN}px)`,
        } : undefined}
      >

        {/* Header */}
        <div className="border-b p-4 flex justify-between items-center shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">{t('notesTitle')}</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMaximized(v => !v)}
              className="text-gray-400 hover:text-gray-500"
              title={isMaximized ? t('notesMinimize') : t('notesMaximize')}
            >
              {isMaximized ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
            </button>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* MD Toolbar */}
        <div className="border-b px-4 py-2 flex gap-1 flex-wrap items-center shrink-0">
          {!showPreview && <>
            <MenuButton onClick={() => toggleMarker('**')} title={t('mdBold')}><BoldIcon className="h-5 w-5" /></MenuButton>
            <MenuButton onClick={() => toggleMarker('*')} title={t('mdItalic')}><ItalicIcon className="h-5 w-5" /></MenuButton>
            <MenuButton onClick={() => toggleMarker('~~')} title={t('mdStrikethrough')}><Strikethrough className="h-5 w-5" /></MenuButton>
            <MenuButton onClick={() => insertMd('[', '](url)')} title={t('mdLink')}><LinkIcon className="h-5 w-5" /></MenuButton>
            <div className="w-px bg-gray-200 mx-1" />
            <MenuButton onClick={() => togglePrefix('## ')} title={t('mdHeading2')}><Heading2 className="h-5 w-5" /></MenuButton>
            <MenuButton onClick={() => togglePrefix('### ')} title={t('mdHeading3')}><Heading3 className="h-5 w-5" /></MenuButton>
            <MenuButton onClick={() => togglePrefix('- ')} title={t('mdBulletList')}><List className="h-5 w-5" /></MenuButton>
            <MenuButton onClick={() => togglePrefix('1. ')} title={t('mdNumberedList')}><ListOrdered className="h-5 w-5" /></MenuButton>
            <MenuButton onClick={() => togglePrefix('- [ ] ')} title={t('mdTaskList')}><ListTodo className="h-5 w-5" /></MenuButton>
            <MenuButton onClick={() => togglePrefix('> ')} title={t('mdQuote')}><Quote className="h-5 w-5" /></MenuButton>
            <MenuButton onClick={() => shiftLines(-1)} title={t('mdOutdent')}><Outdent className="h-5 w-5" /></MenuButton>
            <MenuButton onClick={() => shiftLines(1)} title={t('mdIndent')}><Indent className="h-5 w-5" /></MenuButton>
            <div className="w-px bg-gray-200 mx-1" />
            <MenuButton onClick={() => toggleMarker('`')} title={t('mdInlineCode')}><Code2 className="h-5 w-5" /></MenuButton>
            <MenuButton onClick={() => insertMd('```\n', '\n```')} title={t('mdCodeBlock')}><Code className="h-5 w-5" /></MenuButton>
            <div className="w-px bg-gray-200 mx-1" />
            <div className="relative">
              <MenuButton onClick={() => setShowTablePopover(v => !v)} title={t('mdTable')}><TableIcon className="h-5 w-5" /></MenuButton>
              {showTablePopover && (
                <>
                  {/* click-away backdrop */}
                  <div className="fixed inset-0 z-10" onClick={() => setShowTablePopover(false)} />
                  <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-48">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('mdTableColumns')}</label>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={tableCols}
                      autoFocus
                      onChange={(e) => setTableCols(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          insertTable(tableCols);
                          setShowTablePopover(false);
                        }
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-2"
                    />
                    <button
                      onClick={() => { insertTable(tableCols); setShowTablePopover(false); }}
                      className="w-full px-3 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
                    >
                      {t('mdInsertTable')}
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="w-px bg-gray-200 mx-1" />
          </>}
          <button
            onClick={() => setShowPreview(v => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm font-medium transition-colors ${
              showPreview
                ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            title={showPreview ? t('notesBackToEdit') : t('notesShowPreview')}
          >
            {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            <span>{showPreview ? t('edit') : t('notesPreviewLabel')}</span>
          </button>
        </div>

        {/* AI Assistant */}
        <NotesAIAssistant existingContent={content} onResult={handleAIResult} />

        {/* Revert AI change bar */}
        {preAIContent !== null && (
          <div className="px-4 py-2 border-b bg-indigo-50 flex items-center justify-between gap-2 shrink-0">
            <span className="flex items-center gap-1.5 text-sm text-indigo-800">
              <Sparkles className="h-4 w-4 shrink-0" />
              {t('aiUpdatedNote')}
            </span>
            <button
              onClick={revertAIResult}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm font-medium text-indigo-700 hover:bg-indigo-100 transition-colors shrink-0"
            >
              <Undo2 className="h-4 w-4" />
              <span>{t('revertAIChanges')}</span>
            </button>
          </div>
        )}

        {/* Edit / Preview — obě vrstvy jsou vždy v DOMu, jen se přepíná viditelnost */}
        <div className={`p-4 ${fillsHeight ? 'flex-1 min-h-0 overflow-hidden flex flex-col' : ''}`}>
          <textarea
            ref={textareaRef}
            value={content}
            onChange={handleTextareaChange}
            onKeyDown={handleTextareaKeyDown}
            className={`w-full resize-none font-mono text-sm focus:outline-none leading-relaxed ${
              fillsHeight ? 'flex-1 h-full' : 'min-h-[300px] max-h-[400px] overflow-y-auto'
            } ${showPreview ? 'hidden' : ''}`}
            placeholder={t('notesPlaceholder')}
          />
          {showPreview && (
            <div className={`prose prose-sm max-w-none overflow-y-auto ${fillsHeight ? 'flex-1 h-full' : 'min-h-[300px] max-h-[400px]'}`}>
              {content
                ? <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={MD_COMPONENTS}>{content}</ReactMarkdown>
                : <p className="text-gray-400 italic">{t('notesEmpty')}</p>
              }
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end space-x-2 shrink-0">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            {t('cancel')}
          </button>
          <button
            onClick={() => onSave(content)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('save')}
          </button>
        </div>

        {/* Resize handle (desktop only; hidden while maximized) */}
        {!isMaximized && (
          <div
            onPointerDown={handleResizeStart}
            onPointerMove={handleResizeMove}
            onPointerUp={handleResizeEnd}
            onPointerCancel={handleResizeEnd}
            onDoubleClick={resetSize}
            title={t('notesResize')}
            className="hidden sm:block absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize touch-none text-gray-300 hover:text-gray-500"
          >
            <svg viewBox="0 0 20 20" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M16 8 L8 16 M16 12 L12 16" />
            </svg>
          </div>
        )}

        {/* Unsaved changes dialog */}
        {showUnsavedDialog && (
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center rounded-lg z-10">
            <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-sm w-full">
              <h4 className="text-base font-semibold text-gray-900 mb-2">{t('unsavedChangesTitle')}</h4>
              <p className="text-sm text-gray-600 mb-5">{t('unsavedChangesMessage')}</p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setShowUnsavedDialog(false); onClose(); }}
                  className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  {t('unsavedDiscard')}
                </button>
                <button
                  onClick={() => setShowUnsavedDialog(false)}
                  className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300"
                >
                  {t('unsavedGoBack')}
                </button>
                <button
                  onClick={() => onSave(content)}
                  className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  {t('save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
